import { useEffect, useRef } from 'react'
import { cn } from '@/lib/cn'

/**
 * A native checkbox (Space toggles, the browser announces «mixed» for `indeterminate`). The
 * indeterminate state is a DOM property only, set from an effect; `data-state` carries it into
 * the markup for tests. With `label` the text is visible beside the box; with `ariaLabel` only a
 * screen reader reads it (a row checkbox next to the model's name).
 */
export function Checkbox({
  checked,
  indeterminate = false,
  onChange,
  label,
  ariaLabel,
  disabled,
  className,
}: {
  checked: boolean
  indeterminate?: boolean
  /** `shift` — the Shift key was held (range selection) */
  onChange: (checked: boolean, shift: boolean) => void
  label?: React.ReactNode
  ariaLabel?: string
  disabled?: boolean
  className?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])

  const box = (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      aria-label={label === undefined ? ariaLabel : undefined}
      data-state={indeterminate ? 'mixed' : checked ? 'checked' : 'unchecked'}
      onChange={(e) => onChange(e.target.checked, (e.nativeEvent as MouseEvent).shiftKey === true)}
      className="size-4 shrink-0 cursor-pointer accent-cyan disabled:cursor-default"
    />
  )
  if (label === undefined) return <span className={cn('inline-flex items-center', className)}>{box}</span>
  return (
    <label
      className={cn(
        'inline-flex min-h-8 cursor-pointer items-center gap-2 text-body text-ink-1',
        disabled && 'cursor-default opacity-40',
        className,
      )}
    >
      {box}
      <span>{label}</span>
    </label>
  )
}
